import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { listenToUserData, logout, checkIsOwner } from '../redux/authSlice';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/app/logo.png';
import {
  Bell, Camera, Shield, ShieldCheck, Headphones, Pencil,
  Settings, LogOut, Plus, ChevronRight, Wallet, BarChart2,
  UserCheck, MapPin, Package, ArrowLeft,
} from 'lucide-react';
import { Money } from '@phosphor-icons/react/dist/ssr';

/* ═══════════════════════════════════════════════════════════════════
   THEME: classy black & red, same tokens as the Shop page
   ═══════════════════════════════════════════════════════════════════ */
const A = {
  ink: '#000', red: '#E10000', redDeep: '#B30000', body: '#5C5C5C', line: '#E5E5E5',
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');

.pf, .pf *, .pf *::before, .pf *::after { box-sizing:border-box; font-family:'Jost', system-ui, sans-serif; }
.pf { color:${A.ink}; background:#fff; min-height:100vh; -webkit-font-smoothing:antialiased; overflow-x:hidden; }
.pf h1, .pf h2, .pf h3 { font-family:'Cormorant Garamond', serif; font-weight:500; margin:0; letter-spacing:.01em; }
.pf p { margin:0; }
/* :where keeps this reset at zero specificity so the button classes below always win */
:where(.pf) button { cursor:pointer; border:none; background:none; font-family:inherit; color:inherit; }
.pf button:focus-visible { outline:2px solid ${A.red}; outline-offset:3px; }

.pf-mono { font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:${A.body}; }

.pf-mobile-only { display:block; }
.pf-desktop-only { display:none; }
@media (min-width:900px) {
  .pf-desktop-only { display:block !important; }
  .pf-mobile-only  { display:none !important; }
}

/* ═══ ROUND ICON BUTTON ═══ */
.pf-round {
  position:relative; width:46px; height:46px; border-radius:50%; flex-shrink:0;
  border:1px solid ${A.ink}; background:#fff; display:flex; align-items:center; justify-content:center;
  box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.ink}; transition:all .25s;
}
.pf-round:hover { border-color:${A.red}; color:${A.red}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.red}; }

/* ═══ CARD LOOK (rows + plates share the double-border) ═══ */
.pf-row, .pf-plate {
  position:relative; width:100%; text-align:left; background:#fff;
  border:1px solid ${A.ink}; border-radius:22px;
}
.pf-row::after, .pf-plate::after {
  content:""; position:absolute; inset:4px; border:1px solid ${A.ink}; border-radius:18px; pointer-events:none; transition:border-color .25s;
}
.pf-plate { padding:20px 22px; margin-bottom:12px; }

.pf-row {
  display:flex; align-items:center; gap:14px; padding:14px 20px; margin-bottom:12px;
  transition:transform .3s, border-color .25s;
}
.pf-row:hover { transform:translateY(-3px); border-color:${A.red}; }
.pf-row:hover::after { border-color:${A.red}; }
.pf-row:active { transform:translateY(0); }
.pf-row-label { font-family:'Cormorant Garamond', serif; font-weight:500; font-size:22px; line-height:1.1; }
.pf-row-sub   { font-size:12px; color:${A.body}; margin-top:3px; letter-spacing:.02em; }
.pf-row-badge { font-size:9.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; background:${A.red}; color:#fff; border-radius:999px; padding:5px 11px; flex-shrink:0; }
.pf-chev { display:flex; color:${A.body}; transition:color .2s, transform .2s; }
.pf-row:hover .pf-chev { color:${A.red}; transform:translateX(2px); }

/* ═══ BEAD ═══ */
.pf-bead {
  width:42px; height:42px; border-radius:50%; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center;
  border:1px solid ${A.ink}; background:#fff; color:${A.ink}; transition:all .25s;
}
.pf-row:hover .pf-bead { border-color:${A.red}; color:${A.red}; }
.pf-bead.ok { background:${A.ink}; color:#fff; }
.pf-row:hover .pf-bead.ok { background:${A.red}; color:#fff; }

/* ═══ PILL ═══ */
.pf-pill { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:999px; font-size:10px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; }
.pf-pill.ok      { background:${A.ink}; color:#fff; }
.pf-pill.pending { border:1px solid #CFCFCF; color:${A.body}; }

/* ═══ AVATAR ═══ */
.pf-avatar-wrap { position:relative; flex-shrink:0; }
.pf-avatar-ring { padding:4px; border-radius:50%; background:#fff; border:1px solid ${A.ink}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.ink}; display:inline-flex; }
.pf-avatar      { display:block; border-radius:50%; object-fit:cover; background:#F4F4F4; }
.pf-cam {
  position:absolute; bottom:2px; right:2px; width:30px; height:30px; border-radius:50%;
  background:${A.ink}; color:#fff; display:flex; align-items:center; justify-content:center; border:2px solid #fff; transition:background .2s, transform .2s;
}
.pf-cam:hover { background:${A.red}; transform:scale(1.08); }
.pf-ver {
  position:absolute; top:0; left:0; width:26px; height:26px; border-radius:50%;
  background:${A.red}; color:#fff; display:flex; align-items:center; justify-content:center; border:2px solid #fff;
}

/* ═══ COMPLETION BAR ═══ */
.pf-completion { margin-top:12px; max-width:240px; }
.pf-track { height:4px; border-radius:999px; background:${A.line}; margin-top:7px; overflow:hidden; }
.pf-track i { display:block; height:100%; background:${A.red}; border-radius:999px; }

/* ═══ MOBILE HEADER ═══ */
.pf-header { padding:0 20px 8px; }
.pf-topbar { display:flex; align-items:center; justify-content:space-between; gap:12px; padding-top:calc(30px + env(safe-area-inset-top)); margin-bottom:28px; }
.pf-title  { font-size:clamp(40px,10vw,56px); line-height:1; }
.pf-spark  { color:${A.red}; font-size:.4em; vertical-align:super; margin-left:6px; }
.pf-idrow  { display:flex; align-items:center; gap:18px; }
.pf-name   { font-family:'Cormorant Garamond', serif; font-size:30px; font-weight:500; line-height:1.05; }
.pf-email  { font-size:13px; color:${A.body}; margin:4px 0 10px; }

.pf-body { padding:26px 20px calc(70px + env(safe-area-inset-bottom)); max-width:520px; margin:0 auto; }
.pf-section-title { font-size:12px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:${A.body}; margin:0 4px 12px; }

/* ═══ ADDRESS CARD ═══ */
.pf-addr-head  { display:flex; align-items:center; gap:12px; }
.pf-addr-line1 { font-size:15px; font-weight:500; margin:14px 0 3px; }
.pf-addr-line2 { font-size:13px; color:${A.body}; margin-bottom:16px; }
.pf-addr-empty { text-align:center; padding:10px 4px 2px; }
.pf-ghost-btn {
  display:inline-flex; align-items:center; justify-content:center; gap:8px; height:44px; padding:0 22px; border-radius:999px;
  border:1px solid ${A.ink}; background:#fff; color:${A.ink};
  font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; transition:all .25s;
}
.pf-ghost-btn:hover { border-color:${A.red}; color:${A.red}; transform:translateY(-1px); }

/* ═══ LOGOUT ═══ */
.pf-logout {
  width:100%; height:52px; display:flex; align-items:center; justify-content:center; gap:10px; margin-top:8px;
  border-radius:999px; background:${A.ink}; color:#fff;
  font-size:11.5px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; transition:background .25s, transform .25s;
}
.pf-logout:hover  { background:${A.red}; transform:translateY(-1px); }
.pf-logout:active { background:${A.redDeep}; }

/* ═══ DESKTOP NAV (glass) ═══ */
.pf-nav {
  position:sticky; top:0; z-index:100; height:76px; padding:0 40px;
  display:flex; align-items:center; justify-content:space-between;
  background:rgba(255,255,255,.62); -webkit-backdrop-filter:blur(22px) saturate(180%); backdrop-filter:blur(22px) saturate(180%);
  border-bottom:1px solid rgba(0,0,0,.07); box-shadow:0 10px 30px -20px rgba(0,0,0,.35);
}
.pf-back {
  display:inline-flex; align-items:center; gap:10px; height:46px; padding:0 22px; border-radius:999px; background:#fff; border:1px solid ${A.ink};
  font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.ink}; transition:all .25s;
}
.pf-back:hover { border-color:${A.red}; color:${A.red}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.red}; }
.pf-nav-actions { display:flex; align-items:center; gap:14px; }

/* ═══ DESKTOP HERO ═══ */
.pf-hero {
  position:relative; max-width:1220px; margin:32px auto 0; padding:38px 44px; display:flex; align-items:center; gap:36px;
  border:1px solid ${A.ink}; border-radius:30px; background:#fff;
}
.pf-hero::after { content:""; position:absolute; inset:5px; border:1px solid ${A.ink}; border-radius:25px; pointer-events:none; }
.pf-hero-name  { font-family:'Cormorant Garamond', serif; font-size:48px; font-weight:500; line-height:1; }
.pf-hero-email { font-size:14px; color:${A.body}; margin-top:8px; }

/* ═══ DESKTOP GRID ═══ */
.pf-grid { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:32px; padding:36px 0 80px; max-width:1220px; margin:0 auto; }
.pf-col-header { display:flex; align-items:center; gap:8px; padding:0 4px 12px; margin-bottom:16px; border-bottom:1px solid ${A.line}; }
.pf-col-header i { font-style:normal; color:${A.red}; font-size:11px; }

@media (min-width:900px) and (max-width:1300px) { .pf-nav { padding:0 24px; } .pf-hero, .pf-grid { margin-left:24px; margin-right:24px; } }
@media (prefers-reduced-motion: reduce) {
  .pf *, .pf *::before, .pf *::after { transition:none !important; animation:none !important; }
}
`;

/* ── masking helpers (unchanged) ── */
const maskName = (name: string) => {
  if (!name) return '';
  if (name.length <= 2) return name;
  return name.slice(0, 2) + '*'.repeat(name.length - 2);
};
const maskEmail = (email: string) => {
  if (!email) return '';
  const [u, d] = email.split('@');
  if (!d) return email;
  return `${u.slice(0, 3)}***@${d}`;
};

/* ═══════════════════════════════════════════════════════════════════
   SHARED TAB CONFIG
   One entry, one place: the mobile list flattens this array in order;
   the desktop grid buckets it by `group`.
   ═══════════════════════════════════════════════════════════════════ */
type TabGroup = 'Account' | 'Help' | 'Hosting';

interface ProfileTab {
  key: string;
  icon: React.ReactNode;
  label: string;
  sub: string;
  group: TabGroup;
  onClick: () => void;
  highlight?: boolean;
  badge?: string;
}

const Row: React.FC<{ tab: ProfileTab }> = ({ tab }) => (
  <button type="button" className="pf-row" onClick={tab.onClick}>
    <span className={`pf-bead${tab.highlight ? ' ok' : ''}`}>{tab.icon}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="pf-row-label">{tab.label}</div>
      <div className="pf-row-sub">{tab.sub}</div>
    </div>
    {tab.badge ? (
      <span className="pf-row-badge">{tab.badge}</span>
    ) : tab.highlight ? (
      <ShieldCheck size={17} color={A.ink} />
    ) : (
      <span className="pf-chev"><ChevronRight size={17} /></span>
    )}
  </button>
);

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const isOwner = useSelector((s: RootState) => s.auth.isOwner);
  const userId = user?.userId;

  useEffect(() => { dispatch(listenToUserData()); }, [dispatch]);
  useEffect(() => { dispatch(checkIsOwner()); }, [dispatch]);

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  const profileCompletion = () => {
    const fields = ['firstName', 'lastName', 'username', 'profilePicture', 'userAge', 'address'];
    const filled = fields.filter((f) => {
      const v = f === 'address'
        ? user?.address?.city && user.address?.streetAddress
        : user?.[f as keyof typeof user];
      return !!v;
    });
    return Math.round((filled.length / fields.length) * 100);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isVerified = user?.verifyIdentity === true;
  const completion = profileCompletion();
  const firstName = maskName(user?.firstName || 'First');
  const lastName = maskName(user?.lastName || 'Last');
  const email = maskEmail(user?.email || 'email@example.com');
  // Fallback avatar is the logo mark itself, no generic placeholder image
  const photo = user?.profilePicture || logo;

  const shipping = user?.shippingAddress;
  const hasShippingAddress = !!(shipping && (shipping.streetAddress || shipping.city));

  if (!user) return null;

  const goEdit = () => navigate(`/EditProfile/${userId}`);

  /* ── the single shared config: built once per render, used twice ── */
  const TABS: ProfileTab[] = [
    {
      key: 'orders',
      icon: <Package size={18} />,
      label: 'My Orders',
      sub: 'Track and review your purchases',
      group: 'Account',
      onClick: () => navigate('/orders'),
    },
    ...(isOwner
      ? [{
          key: 'dashboard',
          icon: <BarChart2 size={18} />,
          label: 'Shop Dashboard',
          sub: 'Products, orders & sales',
          group: 'Hosting' as TabGroup,
          onClick: () => navigate('/owner'),
        }]
      : []),
    {
      key: 'edit',
      icon: <Pencil size={18} />,
      label: 'Edit Profile',
      sub: 'Update your info',
      group: 'Account',
      onClick: goEdit,
    },
    {
      key: 'verify',
      icon: isVerified ? <ShieldCheck size={18} /> : <UserCheck size={18} />,
      label: isVerified ? 'Verified' : 'Verify Profile',
      sub: isVerified ? 'Your profile is verified' : 'Complete verification',
      group: 'Account',
      highlight: isVerified,
      onClick: () => {
        if (!userId) { navigate('/AuthSwitch'); return; }
        if (isVerified) return;
        navigate(`/verification/${userId}`);
      },
    },
    {
      key: 'wallet',
      icon: <Wallet size={18} />,
      label: 'Wallet',
      sub: 'Balance & payouts',
      group: 'Account',
      onClick: () => {
        if (!userId) { navigate('/AuthSwitch'); return; }
        navigate(`/Wallet/${userId}`);
      },
    },
    {
      key: 'settings',
      icon: <Settings size={18} />,
      label: 'Settings',
      sub: 'Security & preferences',
      group: 'Account',
      onClick: () => navigate(`/account-settings/${userId}`),
    },
    {
      key: 'security',
      icon: <Shield size={18} />,
      label: 'Login & Security',
      sub: 'Password, sessions & security',
      group: 'Account',
      onClick: () => navigate(`/account-security/${userId}`),
    },
    {
      key: 'notifications',
      icon: <Bell size={18} />,
      label: 'Notifications',
      sub: 'Alerts & updates',
      group: 'Account',
      onClick: () => navigate(`/Notifications/${userId}`),
    },
   
 
    {
      key: 'support',
      icon: <Headphones size={18} />,
      label: 'Customer Service',
      sub: 'Get help instantly',
      group: 'Help',
      badge: 'Chat',
      onClick: () => navigate('/Customer-Support'),
    },
  ];

  const GROUPS: TabGroup[] = ['Account', 'Help', 'Hosting'];

  const AddressCard = () => (
    <div className="pf-plate">
      <div className="pf-addr-head">
        <span className="pf-bead"><MapPin size={17} /></span>
        <span className="pf-row-label">My Addresses</span>
      </div>
      {hasShippingAddress ? (
        <>
          <p className="pf-addr-line1">{shipping!.streetAddress}</p>
          <p className="pf-addr-line2">
            {[shipping!.city, shipping!.state, shipping!.country].filter(Boolean).join(', ')}
            {shipping!.zipCode ? ` ${shipping!.zipCode}` : ''}
          </p>
          <button type="button" className="pf-ghost-btn" onClick={goEdit}>
            <Pencil size={14} /> Edit Address
          </button>
        </>
      ) : (
        <div className="pf-addr-empty">
          <p className="pf-addr-line2" style={{ marginTop: 12 }}>No delivery address on file yet.</p>
          <button type="button" className="pf-ghost-btn" onClick={goEdit}>
            <Plus size={14} /> Add a New Address
          </button>
        </div>
      )}
    </div>
  );

  const VerifiedPill = () => (
    <span className={`pf-pill ${isVerified ? 'ok' : 'pending'}`}>
      {isVerified ? <ShieldCheck size={12} /> : <Shield size={12} />}
      {isVerified ? 'Verified' : 'Not Verified'}
    </span>
  );

  return (
    <div className="pf">
      <style>{CSS}</style>

      {/* ══════════ MOBILE ══════════ */}
      <div className="pf-mobile-only">
        <div className="pf-header">
          <div className="pf-topbar">
            <button type="button" className="pf-round" aria-label="Back" onClick={() => navigate('/')}>
              <ArrowLeft size={19} strokeWidth={1.8} />
            </button>
            <h1 className="pf-title">Profile<span className="pf-spark">✦</span></h1>
            <button type="button" className="pf-round" aria-label="Notifications" onClick={() => navigate(`/Notifications/${userId}`)}>
              <Bell size={19} strokeWidth={1.8} />
            </button>
          </div>

          <div className="pf-idrow">
            <div className="pf-avatar-wrap">
              <div className="pf-avatar-ring">
                <img
                  src={photo}
                  alt="avatar"
                  className="pf-avatar"
                  style={{ width: 82, height: 82 }}
                  onError={(e) => { (e.target as HTMLImageElement).src = logo; }}
                />
              </div>
              <button type="button" className="pf-cam" aria-label="Change photo" onClick={goEdit}>
                <Camera size={13} />
              </button>
              {isVerified && <div className="pf-ver"><ShieldCheck size={13} /></div>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pf-name">{firstName} {lastName}</div>
              <div className="pf-email">{email}</div>
              <VerifiedPill />
              <div className="pf-completion">
                <span className="pf-mono">{completion}% complete</span>
                <div className="pf-track"><i style={{ width: `${completion}%` }} /></div>
              </div>
            </div>
          </div>
        </div>

        <div className="pf-body">
          <div className="pf-section-title">My Addresses</div>
          <AddressCard />

          {TABS.map((tab) => <Row key={tab.key} tab={tab} />)}

          <button type="button" className="pf-logout" onClick={handleLogout}>
            <LogOut size={17} /> Logout
          </button>
        </div>
      </div>

      {/* ══════════ DESKTOP ══════════ */}
      <div className="pf-desktop-only">
        <nav className="pf-nav">
          <button type="button" className="pf-back" onClick={() => navigate('/')}>
            <ArrowLeft size={16} /> Back
          </button>
          <div className="pf-nav-actions">
            <button type="button" className="pf-round" aria-label="Orders" onClick={() => navigate('/orders')}>
              <Package size={19} strokeWidth={1.8} />
            </button>
            <button type="button" className="pf-round" aria-label="Notifications" onClick={() => navigate(`/Notifications/${userId}`)}>
              <Bell size={19} strokeWidth={1.8} />
            </button>
            <button type="button" className="pf-round" aria-label="Login and security" onClick={() => navigate(`/account-security/${userId}`)}>
              <Shield size={19} strokeWidth={1.8} />
            </button>
            <button type="button" className="pf-round" aria-label="Settings" onClick={() => navigate(`/account-settings/${userId}`)}>
              <Settings size={19} strokeWidth={1.8} />
            </button>
          </div>
        </nav>

        <div className="pf-hero">
          <div className="pf-avatar-wrap">
            <div className="pf-avatar-ring">
              <img
                src={photo}
                alt="avatar"
                className="pf-avatar"
                style={{ width: 112, height: 112 }}
                onError={(e) => { (e.target as HTMLImageElement).src = logo; }}
              />
            </div>
            <button type="button" className="pf-cam" aria-label="Change photo" style={{ width: 34, height: 34 }} onClick={goEdit}>
              <Camera size={15} />
            </button>
            {isVerified && <div className="pf-ver" style={{ width: 30, height: 30 }}><ShieldCheck size={15} /></div>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pf-hero-name">{firstName} {lastName}<span className="pf-spark">✦</span></div>
            <div className="pf-hero-email">{email}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 16 }}>
              <VerifiedPill />
              <div className="pf-completion" style={{ marginTop: 0, flex: 1 }}>
                <span className="pf-mono">{completion}% complete</span>
                <div className="pf-track"><i style={{ width: `${completion}%` }} /></div>
              </div>
            </div>
          </div>
        </div>

        <div className="pf-grid">
          {GROUPS.map((group) => (
            <div key={group}>
              <div className="pf-col-header"><i>✦</i><span className="pf-mono">{group}</span></div>

              {group === 'Account' && <AddressCard />}
              {TABS.filter((t) => t.group === group).map((tab) => <Row key={tab.key} tab={tab} />)}

              {group === 'Account' && (
                <button type="button" className="pf-logout" onClick={handleLogout}>
                  <LogOut size={17} /> Logout
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Profile;